/// <amd-module name="TFS/Admin/Groups/Pivots/Settings/Components/SettingsGroupImageCard" />
import * as React from "react";
import { FileSelectorInput } from "TFS/Admin/Common/Controls/FileSelectorInput/FileSelectorInput";
import * as Resources from "TFS/Admin/Groups/Pivots/Settings/Resources";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { KeyCode } from "VSSUI/Util";
import { IVssPersonaProps, VssPersona } from "VSSUI/VssPersona";

interface SettingsGroupImageCardProps {
    pageContext: IVssPageContext;
    disabled: boolean;
    /**
     * Callback after save button is clicked.
     */
    onSave: (resetImage: boolean, imageData: ImageData) => void;
    /**
     * URL of the default avatar that appears on the dialog after loading.
     */
    onLoadDisplayAvatarUrl: string;
    /**
     * Optional callback on persona image load error
     */
    showInitialsOnImageError?: boolean;
    /**
     * Optional, if provided the name to generate initials to use for the persona.
     */
    displayName?: string;
    /**
     * Optional, custom URL of the avatar that appears upon reset. (optional)
     */
    defaultResetAvatarUrl?: string;
    /**
     * User or Group or Team entity type to distinguish title
     */
    entityType: string;
}

export interface InputFileSelect {
    error?: string;
    previewImage?: boolean;
}

export interface ImageData {
    /**
     * URL representing the image data as a base64 encoded string.
     */
    url: string;
    /**
     * Raw binary data from the image.
     */
    byteArray: number[];
}

interface SettingsGroupImageCardState {
    hasImageChanged: boolean;
    error?: string;
    previewSelectedImage?: boolean;
    updateInProgress?: boolean;
    resetClicked: boolean;
}

export class SettingsGroupImageCard extends React.Component<SettingsGroupImageCardProps, SettingsGroupImageCardState> {
    private _fileSelectorInput: FileSelectorInput;
    private validImageTypes: string[] = [".jpg", ".jpeg", ".png"];
    private avatarPersona: IVssPersonaProps;
    private defaultResetAvatarUrl: string;
    private selectedImageData: ImageData;
    private _uploadImgBtn: Button;
    public readonly maxFileSize: number = 2.5 * 1024 * 1024; // 2.5 MB
    private readonly descriptionId = "image-label";

    constructor(props: SettingsGroupImageCardProps) {
        super(props);

        this.defaultResetAvatarUrl =
            this.props.defaultResetAvatarUrl ||
            (!this.props.showInitialsOnImageError
                ? (() => {
                      return getAssetLocation(this.props.pageContext, "ms.vss-admin-web/admin-common-content/team.svg");
                  })()
                : "");

        this.state = {
            hasImageChanged: false,
            updateInProgress: false,
            previewSelectedImage: false,
            error: "",
            resetClicked: false
        };
    }

    public render(): JSX.Element {
        return (
            <>
                <Card titleProps={{ text: format(Resources.GroupImageCardTitle, this.props.entityType), ariaLevel: 2 }}>
                    <div className="flex-column flex-grow rhythm-vertical-16">
                        <div className="flex-row">
                            {this.state.error ? (
                                <MessageBar
                                    severity={MessageBarSeverity.Error}
                                    buttonProps={[
                                        {
                                            onClick: event => {
                                                this._onDismissBtnClick(event);
                                            },
                                            iconProps: { iconName: "Cancel" },
                                            className: "dismiss-btn"
                                        }
                                    ]}
                                >
                                    {this.state.error}
                                </MessageBar>
                            ) : null}
                        </div>
                        <div className="flex-row">
                            <div className="flex-column settings-persona">
                                {this.state.updateInProgress ? (
                                    <React.Fragment>
                                        <VssPersona {...this.avatarPersona} size="extra-large" />
                                    </React.Fragment>
                                ) : this.props.disabled ? (
                                    <VssPersona {...this._getPersona()} size="extra-large" />
                                ) : (
                                    <div
                                        className="persona-list-element profile-card-tab-element image-container"
                                        aria-label={Resources.UploadImageAria}
                                        onClick={this._inputAction}
                                        data-is-focusable={true}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={ev => this.onKeyDown(ev)}
                                    >
                                        <VssPersona {...this._getPersona()} size="extra-large" />
                                        <div className="overlay" />
                                        <Icon iconName="Edit" className="ms-font-l editIcon" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                <div id={`__bolt-${this.descriptionId}`} aria-label={Resources.SelectImageLabel} className="flex-row secondary-text">
                                    {Resources.SelectImageLabel}
                                </div>
                                <div className="flex-row rhythm-horizontal-8">
                                    <Button
                                        iconProps={{ iconName: "Add" }}
                                        text={Resources.ChooseImage}
                                        disabled={this.props.disabled}
                                        onClick={this._inputAction}
                                        ariaDescribedBy={this.descriptionId}
                                        ref={ref => {
                                            this._uploadImgBtn = ref as Button;
                                        }}
                                        ariaLabel={Resources.UploadImageAria}
                                    />
                                    <FileSelectorInput
                                        ref={ref => {
                                            this._fileSelectorInput = ref as FileSelectorInput;
                                        }}
                                        maximumFileSize={this.maxFileSize}
                                        allowedFileExtensions={this.validImageTypes}
                                        onload={(fileDataUrl, fileByteArray, error) => {
                                            this._onFileSelected(fileDataUrl, fileByteArray, error);
                                        }}
                                    />
                                    {!this.props.disabled && (
                                        <Link
                                            className="settings-reset-link font-size"
                                            href="#"
                                            onClick={() => this._onResetClick()}
                                            disabled={this.props.disabled}
                                            role="button"
                                        >
                                            {Resources.ResetLabel}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="settings-disclaimer flex-row rhythm-horizontal-4 body-S secondary-text">
                            <Icon iconName="Info" className="ms-font-s" />
                            <FormatComponent format={Resources.Agreement} elementType="div">
                                <Link target="_blank" rel="noopener noreferrer" href="https://go.microsoft.com/fwlink/?LinkID=266231">
                                    {Resources.TermsOfService}
                                </Link>
                                <Link target="_blank" rel="noopener noreferrer" href="https://go.microsoft.com/fwlink/?LinkID=264782">
                                    {Resources.PrivacyStatement}
                                </Link>
                                <Link target="_blank" rel="noopener noreferrer" href="https://aka.ms/vstscodeofconduct">
                                    {Resources.CodeOfConduct}
                                </Link>
                            </FormatComponent>
                        </div>
                    </div>
                </Card>
            </>
        );
    }

    private onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
        switch (ev.which) {
            case KeyCode.enter:
            case KeyCode.space:
                this._inputAction();
                break;
            default:
                return;
        }
        ev.preventDefault();
        ev.stopPropagation();
    };

    private _onSave(deleteAvatar: boolean): void {
        this.props.onSave(deleteAvatar, this.selectedImageData);
        this.setState({ resetClicked: false, error: "" });
    }

    private async _onFileSelected(fileDataUrl: string, fileByteArray: number[], error: string): Promise<void> {
        this.selectedImageData = { url: fileDataUrl, byteArray: fileByteArray };
        this.setState({ updateInProgress: true, resetClicked: false });
        const result = (await this._onImageFileSelected(error, this.selectedImageData)) as InputFileSelect;
        if (!result.error) {
            this._onSave(false);
        }
        this.setState({ updateInProgress: false, previewSelectedImage: result.previewImage, error: result.error });
    }

    private _onImageFileSelected(error: string, imageData: ImageData): Promise<InputFileSelect> {
        return new Promise(resolve => {
            const result = {} as InputFileSelect;

            // File validation error from image select.
            result.previewImage = !error;
            result.error = error || "";

            return resolve(result);
        });
    }

    private _inputAction = (): void => {
        this._fileSelectorInput.input.click();
    };

    private _onResetClick = (): void => {
        this.selectedImageData = { url: "", byteArray: [] };
        this.setState({ resetClicked: true, error: "" });
        this._onSave(true);
    };

    private _getPersona(): IVssPersonaProps {
        const imageURL = this.state.resetClicked
            ? this.defaultResetAvatarUrl
            : this.state.previewSelectedImage && !this.state.error
            ? this.selectedImageData.url
            : this.props.onLoadDisplayAvatarUrl;

        this.avatarPersona = {
            suppressPersonaCard: true,
            displayName: this.props.displayName,
            showInitialsOnImageError: this.props.showInitialsOnImageError
        };

        if (imageURL) {
            (this.avatarPersona.imageUrl = imageURL), (this.avatarPersona.imgAltText = Resources.ImageAlt);
        }

        return this.avatarPersona;
    }

    private _onDismissBtnClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this.setState({ error: undefined });
        if ((event as React.KeyboardEvent<HTMLSpanElement>).keyCode === KeyCode.enter) {
            this._uploadImgBtn.focus();
        }
    };
}
